package com.example.borshi.web

// Feature Screens
import androidx.compose.runtime.*
import com.example.borshi.web.account.AccountScreen
import com.example.borshi.web.cart.CartScreen
import com.example.borshi.web.cart.CartStore
import com.example.borshi.web.chat.OctopusChatPopup
import com.example.borshi.web.checkout.CheckoutScreen
import com.example.borshi.web.components.BottomNavigation
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.firebase.runFirebaseSmokeTest
import com.example.borshi.web.home.HomeScreen
import com.example.borshi.web.model.Product
import com.example.borshi.web.model.ProteinPlan
import com.example.borshi.web.model.PlanMixItem
import com.example.borshi.web.model.NutritionistBasePlan
import com.example.borshi.web.nutritionist.NutritionistAppEntryPoint
import com.example.borshi.web.orders.OrderConfirmationScreen
import com.example.borshi.web.plans.ProteinPlanDetailScreen
import com.example.borshi.web.plans.ProteinPlansScreen
import com.example.borshi.web.products.ProductsScreen
import com.example.borshi.web.referral.ReferralScreen
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.renderComposable

// Singleton CartStore instance to survive hot reloads
private val globalCartStore by lazy {
    CartStore().apply {
        console.log("🛒 Initializing global CartStore...")
        hydrateFromLocalStorage()
    }
}

fun main() {
    if (window.location.search.contains("smoketest=1")) {
        runFirebaseSmokeTest()
    }

    try {
        console.log("Borshi App: Starting initialization...")

        // Clear loading screen manually
        val root = document.getElementById("root")
        if (root != null) {
            console.log("Root element found, clearing loading screen...")
            root.innerHTML = ""
        }

        // Mark as loaded
        window.asDynamic().__borshiLoaded = true

        console.log("Borshi App: Rendering app...")
        renderComposable(rootElementId = "root") {
            BorshiWebApp()
        }
        console.log("Borshi App: Mounted successfully!")
    } catch (e: Throwable) {
        console.error("Borshi App: Failed to mount!", e)
        val root = document.getElementById("root")
        if (root != null) {
            root.innerHTML = """
                <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;color:#D11243;font-family:sans-serif;text-align:center;padding:20px;">
                    <div style="font-size:48px;margin-bottom:20px;">❌</div>
                    <div style="font-size:20px;margin-bottom:10px;">Failed to Start App</div>
                    <div style="font-size:14px;color:#666;">Check the browser console for error details.</div>
                </div>
            """.trimIndent()
        }
    }
}

@Composable
fun BorshiWebApp() {
    // Use the singleton CartStore instance
    Style(LogoStyles)
    val cartStore = globalCartStore

    // Parse initial URL for deep linking
    val initialScreen = remember {
        parseUrlToScreen(window.location.pathname)
    }

    var currentScreen by remember { mutableStateOf<Screen>(initialScreen) }
    var isChatOpen by remember { mutableStateOf(false) }

    // Handle browser back/forward buttons
    DisposableEffect(Unit) {
        val handlePopState = { _: dynamic ->
            currentScreen = parseUrlToScreen(window.location.pathname)
        }
        window.addEventListener("popstate", handlePopState)

        onDispose {
            window.removeEventListener("popstate", handlePopState)
        }
    }

    // Stable lambdas to prevent unnecessary recompositions that cancel in-flight coroutines
    val navigateWithHistory: (Screen) -> Unit = remember {
        { screen: Screen ->
            currentScreen = screen
            val path = screenToPath(screen)
            if (window.location.pathname != path) {
                window.history.pushState(null, "", path)
            }
        }
    }
    val onAddToCart: (Product) -> Unit = remember(cartStore) { { product: Product -> cartStore.addProduct(product) } }

    Div({
        style {
            width(100.percent)
            height(100.vh)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Color("#F5F5F5"))
            fontFamily("sans-serif")
        }
    }) {
        // Header/Navigation - pass cartStore for cart count
        AppHeader(currentScreen, cartStore, navigateWithHistory)

        // Main Content
        Div({
            style {
                flex(1)
                overflowY("auto")
            }
        }) {
            MainContent(currentScreen, navigateWithHistory, cartStore, onAddToCart)
        }

        // Chat Popup Overlay
        OctopusChatPopup(
            isOpen = isChatOpen,
            onClose = { isChatOpen = false }
        )

        // Chat Button
        FloatingChatButton {
            isChatOpen = !isChatOpen
        }

        // Bottom Navigation (Mobile Only)
        BottomNavigation(
            currentScreen = currentScreen,
            onNavigate = navigateWithHistory
        )
    }
}

@Composable
fun AppHeader(currentScreen: Screen, cartStore: CartStore, onNavigate: (Screen) -> Unit) {
    val cartItemCount = cartStore.itemCount()
    var showInfoMenu by remember { mutableStateOf(false) }

    // Inject header styles once
    DisposableEffect(Unit) {
        if (document.getElementById("bh-styles") != null) return@DisposableEffect onDispose { }
        val s = document.createElement("style")
        s.id = "bh-styles"
        s.textContent = """
.bh{background:rgba(255,255,255,0.97);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border-bottom:1px solid rgba(13,43,46,0.07);box-shadow:0 2px 24px rgba(13,43,46,0.06);display:flex;align-items:center;padding:0 24px;height:64px;gap:8px;position:relative;z-index:100;flex-shrink:0}
.bh-logo{display:flex;align-items:center;gap:10px;cursor:pointer;flex-shrink:0;text-decoration:none;border:none;background:none;padding:0}
.bh-logo-img{width:48px;height:48px;border-radius:50%;object-fit:cover;border:none;flex-shrink:0}
.bh-logo-text{display:flex;flex-direction:column;gap:1px}
.bh-logo-title{font-family:'Syne',sans-serif;font-size:19px;font-weight:800;color:#0D2B2E;letter-spacing:-0.04em;white-space:nowrap}
.bh-logo-tagline{font-family:'DM Sans',sans-serif;font-size:10.5px;font-weight:500;color:#7A9598;white-space:nowrap;letter-spacing:0.01em}
.bh-spacer{flex:1}
.bh-nav{display:flex;align-items:center;gap:2px}
.bh-nb{font-family:'DM Sans',sans-serif;background:none;border:none;padding:8px 13px;font-size:13.5px;font-weight:500;color:#3D5A5E;cursor:pointer;border-radius:8px;white-space:nowrap;transition:all 0.18s ease;line-height:1}
.bh-nb:hover{background:#F0FAFA;color:#0D2B2E}
.bh-nb.bh-active{color:#0D2B2E;font-weight:700;background:#E0F2F1}
.bh-cart{position:relative;background:#0D2B2E;border:none;width:40px;height:40px;border-radius:12px;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:16px;transition:all 0.18s ease;flex-shrink:0;margin-left:6px}
.bh-cart:hover{background:#1A4A4E;transform:translateY(-1px);box-shadow:0 4px 14px rgba(13,43,46,0.22)}
.bh-cart.bh-cf{background:#26A69A}
.bh-badge{position:absolute;top:-5px;right:-5px;background:#D11243;color:#fff;min-width:16px;height:16px;border-radius:8px;font-size:9px;font-weight:800;display:flex;align-items:center;justify-content:center;padding:0 3px;border:2px solid white;font-family:'DM Sans',sans-serif;line-height:1}
.bh-iw{position:relative;flex-shrink:0}
.bh-ib{background:none;border:1.5px solid rgba(13,43,46,0.10);width:36px;height:36px;border-radius:9px;cursor:pointer;font-size:18px;display:flex;align-items:center;justify-content:center;color:#3D5A5E;transition:all 0.18s ease;font-weight:700}
.bh-ib:hover{background:#F0FAFA;border-color:#26A69A;color:#26A69A}
.bh-dd{position:absolute;top:calc(100% + 8px);right:0;background:#fff;border-radius:14px;box-shadow:0 16px 48px rgba(13,43,46,0.14),0 2px 8px rgba(13,43,46,0.07);min-width:196px;overflow:hidden;border:1px solid rgba(13,43,46,0.06);animation:bhDrop 0.16s cubic-bezier(0.22,1,0.36,1) both;z-index:1000}
@keyframes bhDrop{from{opacity:0;transform:translateY(-6px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}
.bh-dh{padding:9px 14px 7px;font-size:9.5px;font-weight:800;letter-spacing:0.12em;text-transform:uppercase;color:#7A9598;border-bottom:1px solid #F0F5F4}
.bh-di{display:flex;align-items:center;gap:10px;padding:10px 14px;font-size:13px;font-weight:500;color:#0D2B2E;text-decoration:none;border-bottom:1px solid #F8FAFA;transition:background 0.1s ease}
.bh-di:hover{background:#F0FAFA}
.bh-di:last-child{border-bottom:none}
@media(max-width:640px){.bh{padding:0 16px;height:56px}.bh-nav{display:none}}
        """.trimIndent()
        document.head?.appendChild(s)
        onDispose { }
    }

    Div({ classes("bh") }) {
        // Logo
        Div({
            classes("bh-logo")
            attr("role", "button")
            attr("aria-label", "Go to Home")
            onClick { onNavigate(Screen.Home) }
        }) {
            Img(
                src = "https://firebasestorage.googleapis.com/v0/b/borshi-6fafe.firebasestorage.app/o/Icons%2Ficon.jpeg?alt=media",
                alt = "Borshi",
                attrs = { classes("bh-logo-img") }
            )
            Div({ classes("bh-logo-text") }) {
                Span({ classes("bh-logo-title") }) { Text("Borshi") }
                Span({ classes("bh-logo-tagline") }) { Text("Planned by you, delivered by us") }
            }
        }

        Div({ classes("bh-spacer") }) {}

        // Navigation
        Div({ classes("bh-nav") }) {
            listOf(
                "Home"     to (currentScreen is Screen.Home),
                "Products" to (currentScreen is Screen.Products),
                "Plans"    to (currentScreen is Screen.ProteinPlans),
                "Account"  to (currentScreen is Screen.Account)
            ).forEach { (label, isActive) ->
                Button({
                    if (isActive) classes("bh-nb", "bh-active") else classes("bh-nb")
                    onClick {
                        when (label) {
                            "Home"     -> onNavigate(Screen.Home)
                            "Products" -> onNavigate(Screen.Products())
                            "Plans"    -> onNavigate(Screen.ProteinPlans)
                            "Account"  -> onNavigate(Screen.Account)
                        }
                    }
                }) { Text(label) }
            }

            // Info dropdown
            Div({ classes("bh-iw") }) {
                Button({
                    classes("bh-ib")
                    attr("title", "About & Legal")
                    onClick { showInfoMenu = !showInfoMenu }
                }) { Text("···") }

                if (showInfoMenu) {
                    Div({ classes("bh-dd") }) {
                        Div({ classes("bh-dh") }) { Text("Legal & Support") }
                        PolicyMenuItem("🐟", "About Us",              "/about-us.html")           { showInfoMenu = false }
                        PolicyMenuItem("📞", "Contact Us",             "/contact-us.html")         { showInfoMenu = false }
                        PolicyMenuItem("📄", "Terms & Conditions",     "/terms-and-conditions.html"){ showInfoMenu = false }
                        PolicyMenuItem("↩️", "Cancellation & Refund", "/refund-policy.html")      { showInfoMenu = false }
                    }
                }
            }

            // Cart
            Button({
                if (cartItemCount > 0) classes("bh-cart", "bh-cf") else classes("bh-cart")
                attr("title", if (cartItemCount > 0) "$cartItemCount item${if (cartItemCount > 1) "s" else ""} in cart" else "Cart")
                onClick { onNavigate(Screen.Cart) }
            }) {
                Text("🛒")
                if (cartItemCount > 0) {
                    Span({ classes("bh-badge") }) { Text(cartItemCount.toString()) }
                }
            }
        }
    }
}

@Composable
fun NavButton(text: String, isActive: Boolean, onClick: () -> Unit) {
    // Legacy shim — new header uses inline lambda buttons
    Button({
        style {
            backgroundColor(if (isActive) Color("#E0F2F1") else Color("transparent"))
            color(if (isActive) Color("#0D2B2E") else Color("#3D5A5E"))
            border(0.px)
            padding(8.px, 13.px)
            borderRadius(8.px)
            cursor("pointer")
            fontSize(13.5.px)
            fontWeight(if (isActive) "700" else "500")
            whiteSpace("nowrap")
            property("transition", "all 0.18s ease")
            fontFamily("DM Sans, sans-serif")
        }
        onClick { onClick() }
    }) { Text(text) }
}

@Composable
fun PolicyMenuItem(icon: String, label: String, href: String, onClose: () -> Unit) {
    A(href = href, attrs = {
        classes("bh-di")
        onClick { onClose() }
    }) {
        Span({ style { fontSize(15.px) } }) { Text(icon) }
        Text(label)
    }
}

@Composable
fun FloatingChatButton(onClick: () -> Unit) {
    val isMobile = window.innerWidth <= 600
    DisposableEffect(Unit) {
        val style = kotlinx.browser.document.createElement("style")
        style.textContent = """
            @keyframes octopusImpulse {
                0%, 100% { transform: scale(1); box-shadow: 0 8px 28px rgba(38,166,154,0.35); }
                50% { transform: scale(1.12); box-shadow: 0 10px 36px rgba(38,166,154,0.5); }
            }
            .octopus-chat-btn { animation: octopusImpulse 1.8s ease-in-out infinite; }
        """.trimIndent()
        kotlinx.browser.document.head?.appendChild(style)
        onDispose { kotlinx.browser.document.head?.removeChild(style) }
    }
    Div({
        style {
            position(Position.Fixed)
            bottom(if (isMobile) 76.px else 24.px)
            right(if (isMobile) 16.px else 24.px)
            property("z-index", "999")
        }
    }) {
        Button({
            classes("octopus-chat-btn")
            style {
                width(52.px); height(52.px)
                backgroundColor(Color("#26A69A")) // rgb(38, 166, 154) mint outer circle
                color(Color.white)
                border(0.px)
                borderRadius(50.percent)
                cursor("pointer")
                property("box-shadow", "0 8px 28px rgba(38,166,154,0.35)")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("transition", "all 0.25s cubic-bezier(0.22,1,0.36,1)")
                padding(0.px)
                overflow("hidden")
            }
            onMouseEnter { it.currentTarget.asDynamic().style.animation = "none"; it.currentTarget.asDynamic().style.transform = "translateY(-3px) scale(1.05)" }
            onMouseLeave { it.currentTarget.asDynamic().style.animation = "octopusImpulse 1.8s ease-in-out infinite"; it.currentTarget.asDynamic().style.transform = "none" }
            onClick { onClick() }
        }) {
            Img(src = FirebaseService.getStorageUrl("images/octopus.png"), alt = "Chat with us", attrs = {
                style { width(38.px); height(38.px); property("object-fit", "contain") }
            })
        }
    }
}

@Composable
fun MainContent(
    screen: Screen,
    onNavigate: (Screen) -> Unit,
    cartStore: CartStore,
    onAddToCart: (Product) -> Unit
) {
    when (screen) {
        is Screen.Home -> HomeScreen(onNavigate, cartStore, onAddToCart)
        is Screen.Products -> ProductsScreen(screen.category, cartStore, onAddToCart)
        is Screen.Referral -> ReferralScreen()
        is Screen.Account -> AccountScreen()
        is Screen.ProteinPlans -> {
            val viewPlan: (NutritionistBasePlan) -> Unit = remember(onNavigate) {
                { basePlan: NutritionistBasePlan -> onNavigate(Screen.ProteinPlanDetail(basePlan.toProteinPlan())) }
            }
            ProteinPlansScreen(
                cartStore = cartStore,
                onNavigate = onNavigate,
                onViewPlan = viewPlan
            )
        }
        is Screen.ProteinPlanDetail -> {
            val planId = screen.planId.ifBlank { screen.plan?.planId ?: "" }
            var resolvedPlan by remember(planId) { mutableStateOf(screen.plan) }

            if (resolvedPlan == null && planId.isNotBlank()) {
                LaunchedEffect(planId) {
                    try {
                        val plans = FirebaseService.getPublicPlans()
                        val match = plans.firstOrNull { it.basePlanId == planId }
                        resolvedPlan = match?.toProteinPlan()
                    } catch (_: Throwable) {}
                }
            }

            val plan = resolvedPlan ?: ProteinPlan(
                planId = planId,
                planName = "Loading...",
                description = "",
                price = 0.0,
                duration = "",
                deliveriesPerWeek = 0,
                consultationEligible = true,
                imageUrl = "",
                mixItems = emptyList()
            )
            ProteinPlanDetailScreen(
                plan = plan,
                onBack = { onNavigate(Screen.ProteinPlans) },
                cartStore = cartStore,
                onNavigate = onNavigate
            )
        }
        is Screen.Cart -> {
            CartScreen(
                cartStore = cartStore,
                onContinueShopping = { onNavigate(Screen.Products("All")) },
                onCheckout = { onNavigate(Screen.Checkout) }
             )
         }
         is Screen.Checkout -> {
             CheckoutScreen(
                 cartStore = cartStore,
                 onOrderPlaced = { orderId -> onNavigate(Screen.OrderConfirmation(orderId)) },
                 onRequireLogin = { onNavigate(Screen.Account) }
             )
         }
        is Screen.OrderConfirmation -> {
            OrderConfirmationScreen(
                orderId = screen.orderId,
                onGoHome = { onNavigate(Screen.Home) },
                onViewOrders = { onNavigate(Screen.Account) }
            )
        }
        is Screen.PaymentStatus -> {
            com.example.borshi.web.payment.PaymentStatusScreen(
                orderId = screen.orderId,
                source = screen.source,
                onNavigate = onNavigate
            )
        }
        is Screen.SignUp -> {
            Div({ style { padding(20.px) } }) {
                Text("Screen not implemented yet: ${screen::class.simpleName}")
            }
        }
        is Screen.Nutritionist -> {
            NutritionistAppEntryPoint()
        }
    }
}


sealed class Screen {
    object Home : Screen()
    data class Products(val category: String = "All") : Screen()
    object Account : Screen()
    object Referral : Screen()
    object ProteinPlans : Screen()
    data class ProteinPlanDetail(val plan: ProteinPlan? = null, val planId: String = "") : Screen()
    object Cart : Screen()
    object Checkout : Screen()
    object SignUp : Screen()
    data class OrderConfirmation(val orderId: String?) : Screen()
    data class PaymentStatus(val orderId: String, val source: String? = null) : Screen()
    object Nutritionist : Screen()
}

// Helper function to parse URL to Screen (for deep linking)
fun parseUrlToScreen(path: String): Screen {
    console.log("Parsing URL path: $path")
    val lowerPath = path.lowercase()
    return when {
        path == "/" || path.isEmpty() -> Screen.Home
        lowerPath.startsWith("/products") -> {
            val category = path.substringAfterLast("/", "All")
            Screen.Products(category)
        }
        lowerPath.startsWith("/plans") || lowerPath.startsWith("/protein-plans") -> Screen.ProteinPlans
        lowerPath.startsWith("/referral") || lowerPath.startsWith("/refer") -> Screen.Referral
        lowerPath.startsWith("/cart") -> Screen.Cart
        lowerPath.startsWith("/checkout") -> Screen.Checkout
        lowerPath.startsWith("/account") -> Screen.Account
        lowerPath.startsWith("/nutritionist") -> Screen.Nutritionist
        lowerPath.startsWith("/signup") -> Screen.SignUp
        lowerPath.startsWith("/payment-status") -> {
            fun normalizeOrderId(raw: String?): String {
                if (raw.isNullOrBlank()) return ""
                // Decode and strip accidental angle brackets if pasted from docs/chat.
                val decoded = try {
                    js("decodeURIComponent(raw)") as String
                } catch (_: Throwable) {
                    raw
                }
                return decoded.trim().trim('<', '>')
            }

            fun readOrderIdFromUrl(): String {
                val keys = arrayOf("order_id", "orderId", "order-id", "cf_order_id", "cfOrderId", "order")
                val params = org.w3c.dom.url.URLSearchParams(window.location.search)
                for (key in keys) {
                    val value = normalizeOrderId(params.get(key))
                    if (value.isNotBlank()) return value
                }

                val hash = window.location.hash
                val queryIndex = hash.indexOf('?')
                if (queryIndex >= 0) {
                    val hashParams = org.w3c.dom.url.URLSearchParams(hash.substring(queryIndex + 1))
                    for (key in keys) {
                        val value = normalizeOrderId(hashParams.get(key))
                        if (value.isNotBlank()) return value
                    }
                }
                return ""
            }

            var orderId = readOrderIdFromUrl()
            if (orderId.isBlank()) {
                val stored = js("sessionStorage.getItem('borshi_return_order_id')") as? String
                if (!stored.isNullOrBlank()) {
                    orderId = normalizeOrderId(stored)
                    js("sessionStorage.removeItem('borshi_return_order_id')")
                }
            }
            val params = org.w3c.dom.url.URLSearchParams(window.location.search)
            var source = params.get("source")
            if (source.isNullOrBlank()) {
                source = js("sessionStorage.getItem('borshi_payment_source')") as? String
                if (!source.isNullOrBlank()) js("sessionStorage.removeItem('borshi_payment_source')")
            }
            Screen.PaymentStatus(orderId, source)
        }
        lowerPath.startsWith("/order-confirmation") -> {
            val orderId = path.substringAfterLast('/', "").ifBlank { null }
            Screen.OrderConfirmation(orderId)
        }
        lowerPath.startsWith("/plan/") -> {
            val planId = path.removePrefix("/plan/").trim('/')
            Screen.ProteinPlanDetail(null, planId = planId)
        }
        else -> {
            console.warn("Unknown path: $path, defaulting to Home")
            Screen.Home
        }
    }
}

// Helper function to convert Screen to URL path
fun screenToPath(screen: Screen): String {
    return when (screen) {
        is Screen.Home -> "/"
        is Screen.Products -> if (screen.category == "All") "/products" else "/products/${screen.category}"
        is Screen.ProteinPlans -> "/plans"
        is Screen.Referral -> "/referral"
        is Screen.ProteinPlanDetail -> "/plan/${screen.plan?.planId ?: ""}"
        Screen.Cart -> "/cart"
        Screen.Checkout -> "/checkout"
        is Screen.OrderConfirmation -> screen.orderId?.let { "/order-confirmation/$it" } ?: "/order-confirmation"
        is Screen.PaymentStatus -> "/payment-status?order_id=${screen.orderId}${if (screen.source != null) "&source=${screen.source}" else ""}"
        Screen.Account -> "/account"
        Screen.SignUp -> "/signup"
        Screen.Nutritionist -> "/nutritionist"
    }
}

private fun NutritionistBasePlan.toProteinPlan(): ProteinPlan {
    val mix = this.recommendedProducts.map { rp ->
        PlanMixItem(
            productId = rp.productId,
            productName = rp.productName,
            productImage = FirebaseService.getProductImageUrl(rp.productId),
            productPrice = 0.0,
            qty = if (rp.quantityPerWeek > 0) "${rp.quantityPerWeek}g/week" else rp.frequency
        )
    }
    return ProteinPlan(
        planId = basePlanId.ifBlank { planName },
        planName = planName,
        description = description,
        price = pricingTemplate.weeklyBase,
        duration = "Weekly",
        deliveriesPerWeek = deliveryScheduleTemplate.maxDeliveriesPerWeek,
        consultationEligible = requiresProgressTracking,
        imageUrl = imageUrl,
        mixItems = mix,
        nutritionistId = nutritionistId,
        nutritionistName = nutritionistName
    )
}

object LogoStyles : StyleSheet() {
    val container by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(2.px)
        cursor("pointer")
    }

    val logoCircle by style {
        width(80.px) // Slightly larger than 48px to match previous 100px relative scale, but maybe smaller for header
        height(80.px)
        borderRadius(50.percent)
        property("object-fit", "cover")
        border(2.px, LineStyle.Solid, Color.white)
        property("box-shadow", "0 2px 4px rgba(0,0,0,0.2)")
    }

    val brandName by style {
        fontSize(24.px) // Adjust based on your topbar height
        fontWeight("bold")
        // Your red shadow can go here as a soft glow
        property("text-shadow", "0 2px 4px rgba(255, 0, 0, 0.15)")
    }

    val subtitle by style {
        fontSize(10.px)
        fontWeight("600")
        opacity(0.8)
        letterSpacing(1.px)
        property("text-transform", "uppercase")

        // internal compiler error workaround
        // media("(max-width: 480px)") {
        //    self style { display(DisplayStyle.None) }
        // }
    }
}

@Composable
fun BorshiLogo() {
    Div({ classes(LogoStyles.container) }) {
        Span({ classes(LogoStyles.brandName) }) {
            Text("BORSHI")
        }
        Span({ classes(LogoStyles.subtitle) }) {
            Text("Planned By You, Delivered By Us")
        }
    }
}
